import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import {
    Plus, Save, Trash2, RotateCcw, CheckCircle2, AlertCircle, ChevronUp, ChevronDown,
} from 'lucide-react';
import api from '../../api/api';
import CSICoachingReport from '../../components/CSICoachingReport';
import CSIListSkeleton from '../../components/layout/CSIListSkeleton';
import { formatChoice } from '../../utils/csiReport';

// Fixed focus areas, in the order Alicia described:
// performance, skilling, networking, community (Giving Back), feedback.
// IDs follow the existing app convention (FOCUS_AREA_IDS in the backend).
const FOCUS_AREAS = [
    { id: 2, name: 'Job Performance' },
    { id: 3, name: 'Skilling' },
    { id: 5, name: 'Networking' },
    { id: 4, name: 'Giving Back' },
    { id: 6, name: 'Feedback' },
];

const MAX_PER_QUESTION = 5;
const EMPTY_BAND = { MinScore: 0, MaxScore: 0, Ranking: '', Description: '', CoachingMessage: '', RecommendedAction: '' };

export default function CSIManagement({ standalone = false }) {
    const { isDarkMode } = useTheme();

    const [questionsByArea, setQuestionsByArea] = useState({}); // areaId -> [question rows incl IsActive]
    const [qDrafts, setQDrafts] = useState({});                 // questionId -> edited text (pending)
    const [qToDelete, setQToDelete] = useState(new Set());      // questionIds pending 2nd-delete (soft); disappear until Save
    const [qNew, setQNew] = useState([]);                       // pending new questions [{tempId, area, text}]
    const [bandsByArea, setBandsByArea] = useState({});         // areaId -> [server band rows incl IsActive]
    const [bandDrafts, setBandDrafts] = useState({});           // bandId -> edited fields (pending)
    const [bandToDelete, setBandToDelete] = useState(new Set()); // bandIds pending 2nd-delete (soft); disappear until Save
    const [bandNew, setBandNew] = useState([]);                 // pending new bands [{tempId, area, ...fields}]

    const [activeArea, setActiveArea] = useState(FOCUS_AREAS[0].id);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState(null); // {type:'ok'|'err', msg}

    // Responses view
    const [view, setView] = useState('configure'); // 'configure' | 'responses'
    const [responses, setResponses] = useState(null);
    const [respLoading, setRespLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);       // expanded responseId
    const [answersById, setAnswersById] = useState({});   // responseId -> areas drilldown
    const [respSearch, setRespSearch] = useState('');
    const [respPage, setRespPage] = useState(1);
    const RESP_PAGE_SIZE = 10;


    // ── styling (matches SurveyManagement) ──────────────────────────────────
    // Rendered as a tab INSIDE Survey Management, so no page-level wrapper here.
    const cardBg = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const inputBase = `border rounded-lg px-3 py-2 text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900'}`;
    const inputCls = `w-full ${inputBase}`;
    const subText = isDarkMode ? 'text-slate-400' : 'text-slate-500';

    const flash = useCallback((type, msg) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // ── load ────────────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [qRes, bRes] = await Promise.all([
                api.get('/csi/admin/questions'),
                api.get('/csi/admin/bands'),
            ]);
            const qMap = {};
            (qRes.data?.areas || []).forEach(a => { qMap[a.focusAreaId] = a.items || []; });
            setQuestionsByArea(qMap);

            const bMap = {};       // areaId -> server band rows
            const bDrafts = {};    // bandId -> edited fields
            (bRes.data?.areas || []).forEach(a => {
                bMap[a.focusAreaId] = a.items || [];
                (a.items || []).forEach(b => { bDrafts[b.BandID] = normalizeBand(b); });
            });
            setBandsByArea(bMap);
            setBandDrafts(bDrafts);

            setQDrafts({});
            setQToDelete(new Set());
            setQNew([]);
            setBandToDelete(new Set());
            setBandNew([]);
        } catch (err) {
            flash('err', 'Failed to load CSI data');
        } finally {
            setLoading(false);
        }
    }, [flash]);

    useEffect(() => { load(); }, [load]);

    // ── question actions ──────────────────────────────────────────────────────
    const questions = questionsByArea[activeArea] || [];
    const activeCount = questions.filter(q => q.IsActive).length;
    const maxScore = activeCount * MAX_PER_QUESTION;

    const newQForArea = qNew.filter(n => n.area === activeArea);

    // 1st delete = grey (IsActive=0, hidden from users); Restore brings it back.
    // Immediate, but updates local state only (never a full reload) so pending
    // edits/adds/deletes aren't lost.
    const setQuestionActive = async (q, isActive) => {
        setBusy(true);
        try {
            await api.patch(`/csi/admin/questions/${q.QuestionID}/toggle`, { isActive });
            setQuestionsByArea(prev => ({
                ...prev,
                [activeArea]: (prev[activeArea] || []).map(x => x.QuestionID === q.QuestionID ? { ...x, IsActive: isActive ? 1 : 0 } : x),
            }));
        } catch { flash('err', 'Update failed'); } finally { setBusy(false); }
    };
    // 2nd delete = mark for soft-delete (row disappears); undone by not saving.
    const markDeleteQuestion = (id) => setQToDelete(prev => new Set(prev).add(id));

    const addQuestion = () => setQNew(prev => [...prev, { tempId: `q${Date.now()}${Math.random()}`, area: activeArea, text: '' }]);
    const updateNewQuestion = (tempId, text) => setQNew(prev => prev.map(n => n.tempId === tempId ? { ...n, text } : n));
    const removeNewQuestion = (tempId) => setQNew(prev => prev.filter(n => n.tempId !== tempId));

    // Save Changes commits everything pending: soft-deletes, edits, and new rows.
    const saveQuestions = async () => {
        const toDelete = questions.filter(q => qToDelete.has(q.QuestionID));
        const changed = questions.filter(q => !qToDelete.has(q.QuestionID)
            && (qDrafts[q.QuestionID] ?? q.QuestionText).trim() !== q.QuestionText.trim());
        const news = newQForArea.filter(n => n.text.trim());
        if (changed.some(q => !(qDrafts[q.QuestionID] ?? '').trim())) return flash('err', 'Question text cannot be empty');
        if (!toDelete.length && !changed.length && !news.length) return flash('ok', 'Nothing to save');
        setBusy(true);
        try {
            for (const q of toDelete) await api.delete(`/csi/admin/questions/${q.QuestionID}`);
            for (const q of changed) await api.put(`/csi/admin/questions/${q.QuestionID}`, {
                questionText: (qDrafts[q.QuestionID]).trim(), sortOrder: q.SortOrder,
            });
            let order = questions.reduce((m, q) => Math.max(m, q.SortOrder || 0), 0);
            for (const n of news) { order += 1; await api.post('/csi/admin/questions', { focusAreaId: activeArea, questionText: n.text.trim(), sortOrder: order }); }
            flash('ok', 'Saved');
            await load();
        } catch { flash('err', 'Save failed'); } finally { setBusy(false); }
    };

    // ── band actions ──────────────────────────────────────────────────────────
    const bands = bandsByArea[activeArea] || [];
    const newBandsForArea = bandNew.filter(n => n.area === activeArea);

    const updateBandDraft = (bandId, field, value) =>
        setBandDrafts(prev => ({ ...prev, [bandId]: { ...prev[bandId], [field]: value } }));

    const bandChanged = (b) => {
        const d = bandDrafts[b.BandID];
        if (!d) return false;
        return ['MinScore', 'MaxScore', 'Ranking', 'Description', 'CoachingMessage', 'RecommendedAction']
            .some(f => String(d[f] ?? '') !== String(b[f] ?? ''));
    };

    const setBandActive = async (b, isActive) => {
        setBusy(true);
        try {
            await api.patch(`/csi/admin/bands/${b.BandID}/toggle`, { isActive });
            setBandsByArea(prev => ({
                ...prev,
                [activeArea]: (prev[activeArea] || []).map(x => x.BandID === b.BandID ? { ...x, IsActive: isActive ? 1 : 0 } : x),
            }));
        } catch { flash('err', 'Update failed'); } finally { setBusy(false); }
    };
    const markDeleteBand = (id) => setBandToDelete(prev => new Set(prev).add(id));

    const addBand = () => setBandNew(prev => [...prev, { tempId: `b${Date.now()}${Math.random()}`, area: activeArea, ...EMPTY_BAND }]);
    const updateNewBand = (tempId, field, value) => setBandNew(prev => prev.map(n => n.tempId === tempId ? { ...n, [field]: value } : n));
    const removeNewBand = (tempId) => setBandNew(prev => prev.filter(n => n.tempId !== tempId));

    const saveBands = async () => {
        const toDelete = bands.filter(b => bandToDelete.has(b.BandID));
        const changed = bands.filter(b => !bandToDelete.has(b.BandID) && bandChanged(b));
        const news = newBandsForArea;
        const validate = (b) => {
            if (!(b.Ranking || '').trim()) { flash('err', 'Each band needs a Rating label'); return false; }
            if (Number(b.MinScore) > Number(b.MaxScore)) { flash('err', 'Min cannot exceed Max'); return false; }
            return true;
        };
        for (const b of changed) if (!validate(bandDrafts[b.BandID])) return;
        for (const b of news) if (!validate(b)) return;
        if (!toDelete.length && !changed.length && !news.length) return flash('ok', 'Nothing to save');
        const payload = (d) => ({
            MinScore: Number(d.MinScore) || 0, MaxScore: Number(d.MaxScore) || 0, Ranking: (d.Ranking || '').trim(),
            Description: d.Description || null, CoachingMessage: d.CoachingMessage || null, RecommendedAction: d.RecommendedAction || null,
        });
        setBusy(true);
        try {
            for (const b of toDelete) await api.delete(`/csi/admin/bands/${b.BandID}`);
            for (const b of changed) await api.put(`/csi/admin/bands/${b.BandID}`, { band: payload(bandDrafts[b.BandID]) });
            for (const n of news) await api.post('/csi/admin/bands', { focusAreaId: activeArea, band: payload(n) });
            flash('ok', 'Saved');
            await load();
        } catch { flash('err', 'Save failed'); } finally { setBusy(false); }
    };


    // ── responses (admin/coach view of completed assessments) ─────────────────
    const loadResponses = useCallback(async () => {
        setRespLoading(true);
        try {
            const res = await api.get('/csi/admin/responses');
            setResponses(res.data?.responses || []);
        } catch {
            flash('err', 'Failed to load responses');
            setResponses([]);
        } finally {
            setRespLoading(false);
        }
    }, [flash]);

    const openResponses = () => {
        setView('responses');
        if (responses === null) loadResponses();
    };

    const toggleExpand = async (r) => {
        if (expanded === r.responseId) { setExpanded(null); return; }
        setExpanded(r.responseId);
        if (!answersById[r.responseId]) {
            try {
                const res = await api.get(`/csi/admin/responses/${r.responseId}`);
                setAnswersById(prev => ({ ...prev, [r.responseId]: res.data?.areas || [] }));
            } catch { /* drill-down is non-blocking */ }
        }
    };

    // ── render ────────────────────────────────────────────────────────────────
    const body = (
        <>
            <p className={`text-sm mb-6 ${subText}`}>
                The Career Success Index onboarding assessment. Manage the 0–{MAX_PER_QUESTION} questions and the
                score-band results (ranking, coaching message, recommended action) for each focus area.
            </p>

            {/* Toast */}
                {toast && (
                    <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[300] px-4">
                        <div className={`flex items-center gap-2 rounded-xl px-5 py-3 shadow-lg text-sm font-semibold text-white ${toast.type === 'ok' ? 'bg-primary-600' : 'bg-red-600'}`}>
                            {toast.type === 'ok' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                            {toast.msg}
                        </div>
                    </div>
                )}

                {/* View toggle: Configure vs Responses */}
                <div className="flex gap-2 mb-6">
                    {[['configure', 'Configure'], ['responses', 'Responses']].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => key === 'responses' ? openResponses() : setView('configure')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === key
                                ? 'bg-primary-500 text-white'
                                : isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {view === 'configure' && (
                <>
                {/* Area tabs */}
                <div className={`flex flex-wrap gap-2 border-b mb-6 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    {FOCUS_AREAS.map(area => {
                        const count = (questionsByArea[area.id] || []).filter(q => q.IsActive).length;
                        const isActive = area.id === activeArea;
                        return (
                            <button
                                key={area.id}
                                onClick={() => setActiveArea(area.id)}
                                className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${isActive
                                    ? 'border-primary-500 text-primary-500'
                                    : `border-transparent ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}`}
                            >
                                {area.name}
                                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <CSIListSkeleton isDarkMode={isDarkMode} rows={6} />
                ) : (
                    <div className="space-y-8">
                        {/* ── Questions ── */}
                        <section className={`rounded-2xl border ${cardBg} p-5`}>
                            <div className="mb-4">
                                <h2 className="text-lg font-semibold">Questions</h2>
                                <span className={`text-xs ${subText}`}>{activeCount} active × {MAX_PER_QUESTION} = max score {maxScore} · greyed = hidden from users</span>
                            </div>

                            <div className="space-y-2">
                                {questions.filter(q => !qToDelete.has(q.QuestionID)).length === 0 && newQForArea.length === 0 &&
                                    <p className={`text-sm ${subText}`}>No questions yet. Add one below.</p>}

                                {questions.filter(q => !qToDelete.has(q.QuestionID)).map((q, idx) => {
                                    const draft = qDrafts[q.QuestionID] ?? q.QuestionText;
                                    const grey = !q.IsActive; // hidden from users
                                    return (
                                        <div key={q.QuestionID} className={`flex items-start gap-2 rounded-xl border p-2.5 ${grey ? 'opacity-60 ' : ''}${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                            <span className={`mt-2 text-xs w-5 text-center ${subText}`}>{idx + 1}</span>
                                            <input className={inputCls} value={draft} disabled={grey}
                                                onChange={e => setQDrafts(prev => ({ ...prev, [q.QuestionID]: e.target.value }))}
                                                placeholder="Question text" />
                                            {grey ? (
                                                <>
                                                    <button onClick={() => setQuestionActive(q, true)} disabled={busy} title="Restore (show to users)"
                                                        className="mt-0.5 p-2 rounded-lg bg-primary-500/15 text-primary-500 hover:bg-primary-500/25">
                                                        <RotateCcw size={16} />
                                                    </button>
                                                    <button onClick={() => markDeleteQuestion(q.QuestionID)} title="Delete (removed on Save)"
                                                        className="mt-0.5 p-2 rounded-lg bg-red-500/15 text-red-500 hover:bg-red-500/25">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            ) : (
                                                <button onClick={() => setQuestionActive(q, false)} disabled={busy} title="Delete (grey out to hide from users)"
                                                    className="mt-0.5 p-2 rounded-lg bg-red-500/15 text-red-500 hover:bg-red-500/25">
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* pending new questions (not saved yet) */}
                                {newQForArea.map(n => (
                                    <div key={n.tempId} className={`flex items-start gap-2 rounded-xl border border-dashed p-2.5 ${isDarkMode ? 'border-primary-700' : 'border-primary-300'}`}>
                                        <span className={`mt-2 text-xs w-5 text-center text-primary-500`}>+</span>
                                        <input className={inputCls} value={n.text} autoFocus
                                            onChange={e => updateNewQuestion(n.tempId, e.target.value)}
                                            placeholder="New question text" />
                                        <button onClick={() => removeNewQuestion(n.tempId)} title="Remove"
                                            className="mt-0.5 p-2 rounded-lg bg-red-500/15 text-red-500 hover:bg-red-500/25">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center justify-between gap-3 mt-4">
                                <button onClick={addQuestion}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}>
                                    <Plus size={16} /> Add question
                                </button>
                                <button onClick={saveQuestions} disabled={busy}
                                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap">
                                    <Save size={16} /> Save Changes
                                </button>
                            </div>
                        </section>

                        {/* ── Score bands ── */}
                        <section className={`rounded-2xl border ${cardBg} p-5`}>
                            <div className="mb-4">
                                <h2 className="text-lg font-semibold">Score Bands</h2>
                                <p className={`text-xs mt-0.5 ${subText}`}>The category score is a <b>percentage (0–100)</b>. Each band maps a % range → rating, description, message, and action. Use <b>-1 to -1</b> for the “Needs More Information” state. · greyed = hidden from users</p>
                            </div>

                            <div className="space-y-3">
                                {bands.filter(b => !bandToDelete.has(b.BandID)).length === 0 && newBandsForArea.length === 0 &&
                                    <p className={`text-sm ${subText}`}>No bands yet. Add one below.</p>}

                                {bands.filter(b => !bandToDelete.has(b.BandID)).map(b => {
                                    const d = bandDrafts[b.BandID] || normalizeBand(b);
                                    const grey = !b.IsActive; // hidden from users
                                    return (
                                    <div key={b.BandID} className={`rounded-xl border p-3 ${grey ? 'opacity-60 ' : ''}${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50'}`}>
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <div className="flex items-center gap-1 shrink-0">
                                                <input type="number" className={`${inputBase} w-16`} value={d.MinScore} disabled={grey}
                                                    onChange={e => updateBandDraft(b.BandID, 'MinScore', e.target.value)} />
                                                <span className={subText}>–</span>
                                                <input type="number" className={`${inputBase} w-16`} value={d.MaxScore} disabled={grey}
                                                    onChange={e => updateBandDraft(b.BandID, 'MaxScore', e.target.value)} />
                                            </div>
                                            <div className="flex items-center gap-1 ml-auto sm:ml-0 sm:order-last shrink-0">
                                            {grey ? (
                                                <>
                                                    <button onClick={() => setBandActive(b, true)} disabled={busy} title="Restore (show to users)"
                                                        className="p-2 rounded-lg bg-primary-500/15 text-primary-500 hover:bg-primary-500/25">
                                                        <RotateCcw size={16} />
                                                    </button>
                                                    <button onClick={() => markDeleteBand(b.BandID)} title="Delete (removed on Save)"
                                                        className="p-2 rounded-lg bg-red-500/15 text-red-500 hover:bg-red-500/25">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            ) : (
                                                <button onClick={() => setBandActive(b, false)} disabled={busy} title="Delete (grey out to hide from users)"
                                                    className="p-2 rounded-lg bg-red-500/15 text-red-500 hover:bg-red-500/25">
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                            </div>
                                            <input className={`${inputBase} order-last w-full sm:order-none sm:w-auto sm:flex-1 sm:min-w-[120px]`} placeholder="Rating (e.g. Above Average)" disabled={grey}
                                                value={d.Ranking} onChange={e => updateBandDraft(b.BandID, 'Ranking', e.target.value)} />
                                        </div>
                                        <AutoTextarea className={`${inputCls} mb-2`} placeholder="Description (what this level means)" disabled={grey}
                                            value={d.Description || ''} onChange={e => updateBandDraft(b.BandID, 'Description', e.target.value)} />
                                        <AutoTextarea className={`${inputCls} mb-2`} placeholder="Coaching message" disabled={grey}
                                            value={d.CoachingMessage || ''} onChange={e => updateBandDraft(b.BandID, 'CoachingMessage', e.target.value)} />
                                        <AutoTextarea className={inputCls} placeholder="Recommended action" disabled={grey}
                                            value={d.RecommendedAction || ''} onChange={e => updateBandDraft(b.BandID, 'RecommendedAction', e.target.value)} />
                                    </div>
                                    );
                                })}

                                {/* pending new bands (not saved yet) */}
                                {newBandsForArea.map(n => (
                                    <div key={n.tempId} className={`rounded-xl border border-dashed p-3 ${isDarkMode ? 'border-primary-700' : 'border-primary-300'}`}>
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <div className="flex items-center gap-1 shrink-0">
                                                <input type="number" className={`${inputBase} w-16`} value={n.MinScore}
                                                    onChange={e => updateNewBand(n.tempId, 'MinScore', e.target.value)} />
                                                <span className={subText}>–</span>
                                                <input type="number" className={`${inputBase} w-16`} value={n.MaxScore}
                                                    onChange={e => updateNewBand(n.tempId, 'MaxScore', e.target.value)} />
                                            </div>
                                            <button onClick={() => removeNewBand(n.tempId)} title="Remove"
                                                className="p-2 rounded-lg bg-red-500/15 text-red-500 hover:bg-red-500/25 ml-auto sm:ml-0 sm:order-last shrink-0">
                                                <Trash2 size={16} />
                                            </button>
                                            <input className={`${inputBase} order-last w-full sm:order-none sm:w-auto sm:flex-1 sm:min-w-[120px]`} placeholder="Rating (e.g. Above Average)"
                                                value={n.Ranking} onChange={e => updateNewBand(n.tempId, 'Ranking', e.target.value)} />
                                        </div>
                                        <AutoTextarea className={`${inputCls} mb-2`} placeholder="Description (what this level means)"
                                            value={n.Description || ''} onChange={e => updateNewBand(n.tempId, 'Description', e.target.value)} />
                                        <AutoTextarea className={`${inputCls} mb-2`} placeholder="Coaching message"
                                            value={n.CoachingMessage || ''} onChange={e => updateNewBand(n.tempId, 'CoachingMessage', e.target.value)} />
                                        <AutoTextarea className={inputCls} placeholder="Recommended action"
                                            value={n.RecommendedAction || ''} onChange={e => updateNewBand(n.tempId, 'RecommendedAction', e.target.value)} />
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center justify-between gap-3 mt-4">
                                <button onClick={addBand}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}>
                                    <Plus size={16} /> Add band
                                </button>
                                <button onClick={saveBands} disabled={busy}
                                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap">
                                    <Save size={16} /> Save Changes
                                </button>
                            </div>
                        </section>
                    </div>
                )}
                </>
                )}

                {/* ── Responses view ── */}
                {view === 'responses' && (() => {
                    if (respLoading) return <CSIListSkeleton isDarkMode={isDarkMode} rows={5} />;
                    if (!responses || responses.length === 0) {
                        return <div className={`rounded-2xl border p-10 text-center ${cardBg} ${subText}`}>No one has completed the assessment yet.</div>;
                    }
                    const q = respSearch.trim().toLowerCase();
                    const filtered = q ? responses.filter(r => (r.name || '').toLowerCase().includes(q)) : responses;
                    const totalPages = Math.max(1, Math.ceil(filtered.length / RESP_PAGE_SIZE));
                    const page = Math.min(respPage, totalPages);
                    const paged = filtered.slice((page - 1) * RESP_PAGE_SIZE, page * RESP_PAGE_SIZE);
                    return (
                        <div>
                            <input className={`${inputCls} mb-4`} placeholder="Search by name…"
                                value={respSearch} onChange={e => { setRespSearch(e.target.value); setRespPage(1); }} />

                            {filtered.length === 0 ? (
                                <div className={`text-center py-10 ${subText}`}>No responses match your search.</div>
                            ) : (
                                <div className="space-y-3">
                                    {paged.map(r => {
                                        const isOpen = expanded === r.responseId;
                                        return (
                                            <div key={r.responseId} className={`rounded-2xl border ${cardBg}`}>
                                                <button onClick={() => toggleExpand(r)} className="w-full flex items-center gap-3 p-4 text-left">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-semibold truncate">{r.name}</p>
                                                        <p className={`text-xs ${subText}`}>{r.submittedOn ? new Date(r.submittedOn).toLocaleString() : ''}</p>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <span className="text-sm font-bold">{r.overall?.rating || '—'}</span>
                                                        {r.overallPercent != null && <span className={`block text-xs ${subText}`}>{Math.round(r.overallPercent)}% overall</span>}
                                                    </div>
                                                    {isOpen ? <ChevronUp size={18} className={subText} /> : <ChevronDown size={18} className={subText} />}
                                                </button>

                                                {isOpen && (
                                                    <div className={`border-t p-4 space-y-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                                        {/* the user's full report (same component as the user result page) */}
                                                        <CSICoachingReport
                                                            result={{ results: r.results, overall: r.overall, overallPercent: r.overallPercent, submittedOn: r.submittedOn }}
                                                            isDarkMode={isDarkMode} firstName={r.name} />


                                                        {/* per-question drill-down */}
                                                        {answersById[r.responseId] && (
                                                            <div className="space-y-5">
                                                                {answersById[r.responseId].map(area => (
                                                                    <div key={area.focusAreaId}>
                                                                        <p className="text-xs font-bold uppercase tracking-wide mb-1">{area.focusAreaName}</p>
                                                                        <ul className="space-y-1">
                                                                            {(area.items || []).map(it => (
                                                                                <li key={it.QuestionID} className={`flex items-start gap-2 text-sm ${it.QuestionType === 'validation' ? `mt-2 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}` : ''}`}>
                                                                                    <span className={`shrink-0 h-6 px-1.5 rounded flex items-center justify-center text-xs font-bold whitespace-nowrap ${it.QuestionType === 'validation' ? 'w-28' : 'min-w-[1.5rem]'} ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>{it.Score != null ? it.Score : (it.ChoiceAnswer ? formatChoice(it.ChoiceAnswer) : '—')}</span>
                                                                                    <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{it.QuestionText}</span>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-4 text-sm">
                                    <button disabled={page <= 1} onClick={() => setRespPage(page - 1)}
                                        className={`px-3 py-1.5 rounded-lg border disabled:opacity-40 ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}>Prev</button>
                                    <span className={subText}>Page {page} of {totalPages} · {filtered.length} total</span>
                                    <button disabled={page >= totalPages} onClick={() => setRespPage(page + 1)}
                                        className={`px-3 py-1.5 rounded-lg border disabled:opacity-40 ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}>Next</button>
                                </div>
                            )}
                        </div>
                    );
                })()}
        </>
    );

    // Embedded as a tab inside Survey Management (admin) → bare content.
    // Standalone (coach route) → wrap with page chrome + heading.
    if (standalone) {
        return (
            <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-900'}`}>
                <div className="max-w-5xl mx-auto px-4 py-8">
                    <h1 className="text-2xl md:text-3xl font-bold mb-4">Career Success Index</h1>
                    {body}
                </div>
            </div>
        );
    }
    return <div className="max-w-5xl">{body}</div>;
}

// Textarea that grows to fit its content so nothing is hidden behind an inner
// scrollbar; on mobile the whole band is readable without scrolling a tiny box.
function AutoTextarea({ value, className = '', ...props }) {
    const ref = useRef(null);
    const fit = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, []);
    useLayoutEffect(() => { fit(); }, [value, fit]);
    useEffect(() => {
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [fit]);
    return <textarea ref={ref} value={value} className={`${className} resize-none overflow-hidden min-h-[3rem]`} {...props} />;
}

function normalizeBand(b) {
    return {
        BandID: b.BandID,
        MinScore: b.MinScore ?? 0,
        MaxScore: b.MaxScore ?? 0,
        Ranking: b.Ranking ?? '',
        Description: b.Description ?? '',
        CoachingMessage: b.CoachingMessage ?? '',
        RecommendedAction: b.RecommendedAction ?? '',
    };
}
